import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { usePagination } from '@web/app/shared';
import { type WorkspaceContext, createNavigator, extractRouteParams } from '@web/app/shared/utils/route-utils';

import { agentsKeys, useAgents } from '@codes/agents';

export const useAgentCatalog = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const routeParams = extractRouteParams<WorkspaceContext>(useParams());
    const { workspaceId, projectId } = routeParams;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigator = createNavigator(navigate, { workspaceId, projectId });
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const searchQuery = searchParams.get('search') || '';
    const page = parseInt(searchParams.get('page') || '0', 10);
    const limit = 9;

    const { data: agentsData, isLoading: isAgentsLoading } = useAgents({
        name: searchQuery,
        view: 'admin',
        page,
        limit,
    });

    const totalCount = agentsData?.total || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const { paginationRange } = usePagination({
        totalCount,
        pageSize: limit,
        currentPage: page,
        siblingCount: 1,
    });

    const handleSearchChange = (query: string) => {
        setSearchParams(prev => {
            if (query) {
                prev.set('search', query);
            } else {
                prev.delete('search');
            }
            prev.set('page', '0');
            return prev;
        });
    };

    const handleAddAgent = () => {
        setShowCreateDialog(true);
    };

    const handleChat = (id: string) => {
        navigator.navigateTo(`/agent-catalogs/${id}/chat`);
    };

    const handleSettings = (id: string) => {
        navigator.navigateTo(`/agent-catalogs/${id}/settings`);
    };

    const handleShare = (_id: string) => {
        // TODO: Implement share functionality (see AgentCard for reference pattern)
    };

    const handleDelete = (_id: string) => {
        // TODO: Implement delete functionality (see AgentCard/AgentListItem for reference pattern)
    };

    const handlePageChange = (newPage: number) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        });
    };

    const handleCreateSuccess = () => {
        setShowCreateDialog(false);
        queryClient.invalidateQueries({ queryKey: agentsKeys.all });
    };

    const handleCloseCreateDialog = () => {
        setShowCreateDialog(false);
    };

    return {
        searchQuery,
        page,
        totalPages,
        showCreateDialog,
        agentsData,
        isAgentsLoading,
        paginationRange,

        handleSearchChange,
        handleAddAgent,
        handleChat,
        handleSettings,
        handleShare,
        handleDelete,
        handlePageChange,
        handleCreateSuccess,
        handleCloseCreateDialog,
    };
};
